'use strict';

function normalizeWidgetToken(value) {
	return String(value || '')
		.trim()
		.replace(/[\u2018\u2019\u201C\u201D]/g, '')
		.replace(/^['"]+|['"]+$/g, '')
		.toLowerCase();
}

function getCookie() {
	let match = document.cookie.split('; ').find(function (row) {
		return row.startsWith('CONSENTMGR=');
	});

	return match ? decodeURIComponent(match.split('=')[1]) : null;
}

function getConsentParams() {
	try {
		let cookieStr = getCookie();
		if (!cookieStr) {
			return {};
		}
		return Object.fromEntries(
			cookieStr.split('|').map(function (p) {
				return p.split(':');
			})
		);
	} catch (e) {
		return {};
	}
}

/**
 * CONSENTMGR state for Aurora
 * @returns {'pending'|'declined'|'granted'}
 */
function getAnalyticsConsentState() {
	let params;

	if (!getCookie()) {
		return 'pending';
	}

	params = getConsentParams();

	if (params.consent === 'false') {
		return 'declined';
	}

	if (params.consent === 'true' && String(params.c1) === '1') {
		return 'granted';
	}

	if (params.consent === 'true') {
		return 'declined';
	}

	return 'pending';
}

/**
 * @returns {boolean}
 */
function hasAnalyticsConsent() {
	return getAnalyticsConsentState() === 'granted';
}

function isUtagViewReady() {
	return typeof window.utag !== 'undefined' && typeof window.utag.view === 'function';
}

function sanitizeLinkText(text) {
	if (!text) {
		return '';
	}

	let str = String(text);

	try {
		if (typeof str.normalize === 'function') {
			str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
		}
	} catch (e) {
		// keep original when normalize is unavailable
	}

	return str
		.replace(/&[#\w]+;/g, ' ')
		.replace(/[\u2010-\u2015\u2212]/g, ' ') // typographic dashes / minus → space
		.replace(/[\u00A0\u202F]/g, ' ') // NBSP / narrow space → space
		.replace(/[^\x20-\x7E]/g, ' ') // leftover non-ASCII (emoji, curly quotes, …) → space
		.replace(/['"\n\r\t,!?]/g, '') // PDF-forbidden chars (quotes, newline, tab, comma, ! ?)
		.replace(/-/g, '_')
		.replace(/\s+/g, '_')
		.replace(/_+/g, '_')
		.replace(/^_+|_+$/g, '')
		.toLowerCase()
		.substring(0, 120);
}

function decodeBase64Utf8(value) {
	let binary;
	let bytes;
	let i;

	if (!value || typeof window === 'undefined' || typeof window.atob !== 'function') {
		return '';
	}

	binary = window.atob(value);

	if (typeof TextDecoder !== 'undefined') {
		bytes = new Uint8Array(binary.length);
		for (i = 0; i < binary.length; i++) {
			bytes[i] = binary.charCodeAt(i);
		}
		return new TextDecoder('utf-8').decode(bytes);
	}

	return decodeURIComponent(escape(binary));
}

function collapseRepeatedName(name) {
	let str = String(name || '').trim();
	let parts;
	let i;
	let chunk;
	let compactChunk;
	let rest;

	if (!str) {
		return '';
	}

	parts = str.split(/[\s\-_]+/).filter(Boolean);
	if (
		parts.length >= 2 &&
		parts.every(function (part) {
			return part.toLowerCase() === parts[0].toLowerCase();
		})
	) {
		return parts[0];
	}

	if (str.length < 20) {
		return str;
	}

	for (i = 10; i <= Math.floor(str.length / 2); i++) {
		chunk = str.substring(0, i).replace(/[\s\-_]+$/g, '');
		if (chunk.length < 10) {
			continue;
		}
		compactChunk = chunk.replace(/[\s\-_]+/g, '').toLowerCase();
		if (compactChunk.length < 10) {
			continue;
		}
		rest = str
			.substring(i)
			.replace(/^[\s\-_]+/g, '')
			.replace(/[\s\-_]+/g, '')
			.toLowerCase();
		if (rest.startsWith(compactChunk)) {
			return chunk;
		}
	}

	return str;
}

function normalizeVariantAttrName(name) {
	let token = sanitizeLinkText(name);

	if (token === 'varpack' || token === 'pack' || token === 'packs' || token === 'size') {
		return 'packsize';
	}

	if (token.startsWith('varpack')) {
		return token.substring(3);
	}

	return token;
}

/**
 * Drop duplicated attr prefix from display values (`packsize_16caps` → `16caps`).
 * @param {string} name
 * @param {string} value
 * @returns {string}
 */
function stripVariantAttrValuePrefix(name, value) {
	let attrName = normalizeVariantAttrName(name);
	let token = sanitizeLinkText(value);
	let prefixes = [attrName];
	let p;
	let prefix;

	if (attrName === 'packsize') {
		prefixes.push('varpacksize');
	}
	if (attrName === 'packtype') {
		prefixes.push('varpacktype');
	}

	for (p = 0; p < prefixes.length; p++) {
		prefix = prefixes[p];
		if (prefix && token.startsWith(prefix + '_')) {
			return token.substring(prefix.length + 1);
		}
	}

	return token;
}

let WEIGHT_UNIT_PATTERN = /(\d+(?:\.\d+)?)\s*(g|kg|oz|lb|ml)\b/i;

function hasWeightUnit(value) {
	return WEIGHT_UNIT_PATTERN.test(String(value || ''));
}

/**
 * PDF `$$$variant_name$$$`: color, cups, weight.
 * @param {string} attrName
 * @param {string} sizeValue
 * @returns {string}
 */
function toPdfVariantName(attrName, sizeValue) {
	let name = normalizeVariantAttrName(attrName);

	if (name === 'packtype') {
		return '';
	}

	if (name === 'color' || name === 'weight' || name === 'cups') {
		return name;
	}

	if (name === 'packsize') {
		return hasWeightUnit(sizeValue) ? 'weight' : 'cups';
	}

	return name;
}

/**
 * PDF `$$$variant_value$$$`: white, 16, 250g.
 * @param {string} pdfName
 * @param {string} rawValue
 * @returns {string}
 */
function formatPdfVariantValue(pdfName, rawValue) {
	let weightMatch;
	let digits;
	let token;
	let prefixes;
	let p;
	let prefix;

	if (pdfName === 'weight') {
		weightMatch = String(rawValue || '')
			.toLowerCase()
			.replace(/_/g, ' ')
			.match(WEIGHT_UNIT_PATTERN);
		if (weightMatch) {
			return weightMatch[1] + weightMatch[2];
		}
	}

	if (pdfName === 'cups') {
		digits = String(rawValue || '').match(/(\d+)/);
		if (digits) {
			return digits[1];
		}
	}

	token = sanitizeLinkText(rawValue);
	prefixes = [pdfName, 'color', 'packsize', 'packtype', 'varpacksize', 'varpacktype'];
	for (p = 0; p < prefixes.length; p++) {
		prefix = prefixes[p];
		if (prefix && token.startsWith(prefix + '_')) {
			token = token.substring(prefix.length + 1);
			break;
		}
	}

	return token;
}

/**
 * @param {Array<{name: string, value: string}>} pairs
 * @returns {Array<{name: string, value: string}>}
 */
function sortVariationPairs(pairs) {
	let order = { color: 0, cups: 1, packsize: 1, weight: 2, packtype: 3 };

	return (pairs || []).slice().sort(function sortPairs(a, b) {
		let aName = normalizeVariantAttrName(a.name);
		let bName = normalizeVariantAttrName(b.name);
		let aOrder = Object.prototype.hasOwnProperty.call(order, aName) ? order[aName] : 50;
		let bOrder = Object.prototype.hasOwnProperty.call(order, bName) ? order[bName] : 50;
		return aOrder - bOrder;
	});
}

function encodeBase64Utf8(value) {
	if (typeof window === 'undefined' || typeof window.btoa !== 'function') {
		return '';
	}

	return window.btoa(unescape(encodeURIComponent(String(value || ''))));
}

/**
 * Editorial `data-tealium-*` values may contain HTML or entities; extract plain text for analytics.
 * @param {*} value
 * @returns {string}
 */
function trackingMarkupToPlainText(value) {
	if (value == null || value === '') {
		return '';
	}
	let s = String(value).trim();
	if (!s) {
		return '';
	}
	if (!/[<>&]/.test(s)) {
		return s;
	}
	let tmp = document.createElement('div');
	tmp.innerHTML = s;
	let plain = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
	return (
		plain ||
		s
			.replace(/<[^>]*>/g, ' ')
			.replace(/\s+/g, ' ')
			.trim()
	);
}

/** Plain text from markup, then same serialization as menu/footer (`25%_off_...`). */
function trackingEditorialAttrSerialized(value) {
	return sanitizeLinkText(trackingMarkupToPlainText(value));
}

function getUtagBasePayload() {
	let merged = $.extend({}, window.utag_data || {});
	let pick = window.lavazzaPickDataLayerProps;
	if (typeof pick === 'function') {
		return pick(merged);
	}
	return merged;
}

let PRODUCT_MODULE_KEYS = [
	'product',
	'quantity',
	'price',
	'product_name',
	'product_id',
	'product_img_src',
	'product_currency',
	'product_category',
	'product_category_name',
	'product_variant',
	'product_price',
	'product_sale_percentage',
	'product_promo',
	'product_type',
	'product_callout',
	'product_format',
	'product_display_name',
	'product_powerbrand',
	'product_availability',
	'product_recurring'
];

/**
 * @param {*} value
 * @returns {number|string}
 */
function formatProductNumber(value) {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	let number = Number(value);

	if (isNaN(number)) {
		return '';
	}

	return Math.round(number * 100) / 100;
}

function priceNode(node) {
	let val;

	if (!node) {
		return null;
	}

	// Price models carry `{ value }`; journey/subscription items may carry a bare number.
	val = typeof node === 'object' ? node.value : node;

	if (val != null && val !== '') {
		return { value: val, currency: node.currency || '' };
	}

	return null;
}

/**
 * PDF product_sale_percentage ("20%"). Price factory sets `percentage` on product models only;
 * line items / journey items fall back to list vs sales (same rounding as the factory).
 * @param {Object} price
 * @returns {string}
 */
function resolveSalePercentage(price) {
	let pct = price && price.percentage ? price.percentage.value : null;
	let sales;
	let list;

	if ((pct == null || pct === '') && price && typeof price === 'object') {
		sales = priceNode(price.sales);
		list = priceNode(price.list);
		if (sales && list && Number(list.value) > Number(sales.value)) {
			pct = Math.round((1 - Number(sales.value) / Number(list.value)) * 100);
		}
	}

	pct = formatProductNumber(pct);
	return pct === '' || pct === 0 ? '' : pct + '%';
}

/**
 * Sales first; list / range when the product is OOS or has no sales price.
 * @param {*} price
 * @returns {{value: number|null, currency: string}}
 */
function extractSalesPrice(price) {
	if (price == null || price === '') {
		return { value: null, currency: '' };
	}

	if (typeof price === 'number') {
		return { value: price, currency: '' };
	}

	return (
		priceNode(price.sales) ||
		priceNode(price.list) ||
		priceNode(price.min && price.min.sales) ||
		priceNode(price.max && price.max.sales) ||
		priceNode(price) || { value: null, currency: '' }
	);
}

/**
 * If SFCC wraps an absolute DAM url inside a static path, keep the real http(s) url.
 * @param {string} src
 * @returns {string}
 */
function extractAbsoluteUrl(src) {
	let str = String(src || '').trim();
	let httpsIdx;
	let httpIdx;
	let idx;

	if (!str) {
		return '';
	}

	httpsIdx = str.lastIndexOf('https://');
	httpIdx = str.lastIndexOf('http://');
	idx = httpsIdx > httpIdx ? httpsIdx : httpIdx;

	if (idx > 0) {
		return str.substring(idx);
	}

	return str;
}

/**
 * @param {Object} product
 * @returns {string}
 */
function firstImageUrl(group) {
	let first;

	if (!group) {
		return '';
	}

	if (typeof group === 'string') {
		return extractAbsoluteUrl(group);
	}

	first = group[0] || group;
	if (!first) {
		return '';
	}

	if (typeof first === 'string') {
		return extractAbsoluteUrl(first);
	}

	return extractAbsoluteUrl(String(first.url || first.absURL || first.src || ''));
}

/**
 * @param {Object} product
 * @returns {string}
 */
function resolveProductImageSrc(product) {
	let images;
	let types;
	let i;
	let src;
	let key;

	if (!product) {
		return '';
	}

	if (product.imageSrc || product.product_img_src) {
		return extractAbsoluteUrl(String(product.imageSrc || product.product_img_src));
	}

	images = product.images;
	if (!images) {
		return '';
	}

	if (typeof images === 'string') {
		return extractAbsoluteUrl(images);
	}

	types = ['small', 'medium', 'large'];
	for (i = 0; i < types.length; i++) {
		src = firstImageUrl(images[types[i]]);
		if (src) {
			return src;
		}
	}

	for (key in images) {
		if (Object.prototype.hasOwnProperty.call(images, key)) {
			src = firstImageUrl(images[key]);
			if (src) {
				return src;
			}
		}
	}

	return '';
}

function getSelectedVariationValue(attr) {
	let values;
	let i;

	if (!attr) {
		return null;
	}

	values = attr.values;

	if (values && values.length) {
		for (i = 0; i < values.length; i++) {
			if (values[i] && values[i].selected) {
				return values[i];
			}
		}
		return values[0];
	}

	return null;
}

/**
 * @param {string} raw
 * @returns {{size: string, type: string}}
 */
function parseCombinedPackValue(raw) {
	let str = String(raw || '').trim();
	let parts;

	if (str.indexOf('|') === -1) {
		return { size: str, type: '' };
	}

	parts = str.split('|');
	return {
		type: String(parts[0] || '').trim(),
		size: String(parts[parts.length - 1] || '').trim()
	};
}

/**
 * @param {Object} attr
 * @returns {string}
 */
function getSelectedVariationRawValue(attr) {
	let selected = getSelectedVariationValue(attr);

	if (selected) {
		return String(selected.value || selected.displayValue || '');
	}
	return attr && attr.displayValue ? String(attr.displayValue) : '';
}

/**
 * @param {Object} attr
 * @returns {string}
 */
function getSelectedVariationDisplayValue(attr) {
	let raw = getSelectedVariationRawValue(attr);
	return raw ? parseCombinedPackValue(raw).size || raw : '';
}

/**
 * @param {Array<Object>} variationAttributes
 * @returns {Array<{name: string, value: string}>}
 */
function getSelectedVariationPairs(variationAttributes) {
	let pairs = [];
	let i;
	let attr;
	let name;
	let value;

	if (!variationAttributes || !variationAttributes.length) {
		return pairs;
	}

	for (i = 0; i < variationAttributes.length; i++) {
		attr = variationAttributes[i];
		name = String((attr && attr.attributeId) || '').trim();
		if (!name) {
			name = String((attr && attr.id) || '').trim();
			if (name.indexOf('|') !== -1) {
				name = 'VarPack';
			}
		}
		value = getSelectedVariationDisplayValue(attr);

		if (name && value) {
			// Combined VarPack values (`Capsules|16 caps`) also carry the pack type.
			pairs.push({ name: name, value: value, packType: parseCombinedPackValue(getSelectedVariationRawValue(attr)).type });
		}
	}

	return pairs;
}

/**
 * Selected variation as PDF `product_variant` / `product_format`.
 * @param {Object} product
 * @returns {{variant: string, format: string}}
 */
function getNormalizedVariationState(product) {
	let rawPairs = getSelectedVariationPairs(product && product.variationAttributes);
	let mapped = [];
	let types = [];
	let parts = [];
	let seen = {};
	let i;
	let parsed;
	let sizeValue;
	let pdfName;
	let pdfValue;

	for (i = 0; i < rawPairs.length; i++) {
		parsed = parseCombinedPackValue(rawPairs[i].value);
		sizeValue = parsed.size || rawPairs[i].value;
		pdfName = toPdfVariantName(rawPairs[i].name, sizeValue);
		if (!pdfName) {
			continue;
		}
		pdfValue = formatPdfVariantValue(pdfName, sizeValue);
		if (!pdfValue || seen[pdfName]) {
			continue;
		}
		seen[pdfName] = true;
		mapped.push({ name: pdfName, value: pdfValue });
	}

	mapped = sortVariationPairs(mapped);
	for (i = 0; i < mapped.length; i++) {
		types.push(mapped[i].name);
		parts.push(mapped[i].name + ':' + mapped[i].value);
	}

	return {
		variant: types.join('|'),
		format: parts.join('|')
	};
}

/**
 * Notify-me `type`: `packsize-16caps|packtype-capsules` (selected variant, not plp/pdp).
 * @param {Object} product
 * @returns {string}
 */
function formatNotifyMeVariantType(product) {
	let selected = getSelectedVariationPairs(product && product.variationAttributes);
	let hasPackTypeAttr = selected.some(function (pair) {
		return normalizeVariantAttrName(pair.name) === 'packtype';
	});
	let pairs = [];
	let parts = [];
	let i;
	let name;
	let value;

	selected.forEach(function (pair) {
		pairs.push(pair);
		if (pair.packType && !hasPackTypeAttr) {
			pairs.push({ name: 'packtype', value: pair.packType });
		}
	});
	pairs = sortVariationPairs(pairs);

	for (i = 0; i < pairs.length; i++) {
		name = sanitizeLinkText(normalizeVariantAttrName(pairs[i].name));
		// `12_oz` → `12oz`, matching `16caps`.
		value = stripVariantAttrValuePrefix(pairs[i].name, pairs[i].value).replace(/(\d)_(?=[a-z])/g, '$1');
		if (name && value) {
			parts.push(name + '-' + value);
		}
	}
	return parts.join('|');
}

/**
 * @param {*} value
 * @returns {string}
 */
function resolveClientPowerBrand(product) {
	return sanitizeLinkText((product && (product.coffeeCollection || product.brand)) || '');
}

/**
 * @param {*} value
 * @returns {string}
 */
function normalizeCalloutToken(value) {
	let token = sanitizeLinkText(value);

	if (token === 'newin') {
		return 'new_in';
	}

	if (token === 'bestseller' || token === 'bestsellers') {
		return 'best_seller';
	}

	return token;
}

/**
 * Product tags joined with `|`.
 * @param {Object} product
 * @returns {string}
 */
function resolveProductCallout(product) {
	let tags = (product && product.tags) || {};
	let parts = [];

	if (tags.newIn || normalizeCalloutToken(tags.primaryTag) === 'new_in') {
		parts.push('new_in');
	}
	if (tags.bestSellers || normalizeCalloutToken(tags.primaryTag) === 'best_seller') {
		parts.push('best_seller');
	}
	if (tags.onlineExclusive) {
		parts.push('online_exclusive');
	}
	if (tags.showPromoChip || normalizeCalloutToken(tags.primaryTag) === 'promo') {
		parts.push('promo');
	}

	return parts.join('|');
}

/**
 * PDF product_type: stand-alone_product, bundle, free_gift, subscription.
 * @param {Object} product
 * @returns {string}
 */
function resolveTrackingProductType(product) {
	if (product.isSubscription || product.LVZ_IsSubscription || product.productType === 'subscription') {
		return 'subscription';
	}
	if (product.isFreeGift || product.isBonusProductLineItem) {
		return 'free_gift';
	}
	return product.productType === 'bundle' ? 'bundle' : 'stand-alone_product';
}

/**
 * @param {Object} product
 * @returns {string}
 */
function resolveProductPromo(product) {
	if (!product) {
		return '';
	}

	if (product.renderedPromotions) {
		return sanitizeLinkText(trackingMarkupToPlainText(product.renderedPromotions));
	}

	if (product.tags && product.tags.promoAnnouncement) {
		return sanitizeLinkText(trackingMarkupToPlainText(product.tags.promoAnnouncement));
	}

	return '';
}

/**
 * Parallel arrays for Tealium product module (one index per product).
 * @param {Array<Object>} modules
 * @returns {Object}
 */
function flattenProductModules(modules) {
	let out = {};
	let i;
	let j;
	let key;
	let module;

	for (i = 0; i < PRODUCT_MODULE_KEYS.length; i++) {
		out[PRODUCT_MODULE_KEYS[i]] = [];
	}

	if (!modules || !modules.length) {
		return out;
	}

	for (j = 0; j < modules.length; j++) {
		module = modules[j] || {};

		for (i = 0; i < PRODUCT_MODULE_KEYS.length; i++) {
			key = PRODUCT_MODULE_KEYS[i];
			out[key].push(module[key] == null ? '' : module[key]);
		}
	}

	return out;
}

/**
 * Replace `products: [{}]` with flat arrays on the payload.
 * @param {Object} payload
 * @returns {Object}
 */
function expandProductModuleInPayload(payload) {
	let modules;
	let first;
	let expanded;

	payload = payload || {};
	modules = payload.products;

	if (!Array.isArray(modules) || !modules.length) {
		return payload;
	}

	first = modules[0];

	if (!first || typeof first !== 'object' || Array.isArray(first)) {
		return payload;
	}

	if (first.product_id == null && first.product == null && first.product_name == null) {
		return payload;
	}

	expanded = $.extend({}, payload);
	delete expanded.products;

	// product_impression_type belongs to the products module: one value per product, same index.
	if (expanded.product_impression_type != null && !Array.isArray(expanded.product_impression_type)) {
		expanded.product_impression_type = modules.map(function () {
			return expanded.product_impression_type;
		});
	}

	return $.extend(expanded, flattenProductModules(modules));
}

/**
 * @param {Object} payload
 */
function sendUtagLinkEvent(payload) {
	try {
		if (!hasAnalyticsConsent()) {
			return;
		}

		payload = expandProductModuleInPayload(payload || {});
		let data = $.extend({}, getUtagBasePayload(), payload);

		if (typeof window.utag === 'undefined' || typeof window.utag.link !== 'function') {
			return;
		}

		window.utag.link(data);
	} catch (e) {
		// Tealium must not break storefront interactions
	}
}

/**
 * @param {Object} [extraPayload]
 */
/**
 * SPA steps (checkout, subscription journey) send their own page_name without a reload: chain
 * previous_page_name on them too, with the same sessionStorage keys as the inline script in tealium.isml.
 * @param {Object} data view payload, updated in place
 */
function applyPreviousPageName(data) {
	let pageKey = 'lvzTealiumPageName';
	let previousKey = 'lvzTealiumPreviousPageName';
	let last;

	if (!data.page_name) {
		return;
	}

	try {
		last = window.sessionStorage.getItem(pageKey) || '';
		// Same page_name sent again (e.g. on-load view after the inline script): keep its previous page.
		if (last !== data.page_name) {
			window.sessionStorage.setItem(previousKey, last);
			window.sessionStorage.setItem(pageKey, data.page_name);
		}
		data.previous_page_name = window.sessionStorage.getItem(previousKey) || '';
		if (window.utag_data) {
			window.utag_data.previous_page_name = data.previous_page_name;
		}
	} catch (e) {
		// sessionStorage blocked: keep the server/inline value
	}
}

function sendUtagViewEvent(extraPayload) {
	let data;
	let utagAttempts = 0;
	let intervalId = null;
	// 30s: on a slow connection utag.js (loaded from an external CDN) can take longer than a
	// couple of seconds to become ready — this used to give up after only 5s (50 * 100ms) and
	// silently drop the view event even though everything else about it was correct.
	let UTAG_READY_MAX_ATTEMPTS = 300;
	let POLL_MS = 100;

	try {
		data = $.extend({}, getUtagBasePayload(), expandProductModuleInPayload(extraPayload || {}));
	} catch (e) {
		return;
	}

	applyPreviousPageName(data);

	function stopWatch() {
		if (intervalId) {
			window.clearInterval(intervalId);
			intervalId = null;
		}
	}

	function trySendView() {
		let consentState = getAnalyticsConsentState();

		if (consentState === 'declined') {
			stopWatch();
			return;
		}

		if (consentState !== 'granted') {
			utagAttempts = 0;
			return;
		}

		if (!isUtagViewReady()) {
			utagAttempts += 1;
			if (utagAttempts >= UTAG_READY_MAX_ATTEMPTS) {
				stopWatch();
			}
			return;
		}

		try {
			window.utag.view(data);
		} catch (e) {
			console.error('Error sending Tealium view event:', e);
		}

		stopWatch();
	}

	trySendView();

	if (intervalId || getAnalyticsConsentState() === 'declined') {
		return;
	}

	if (getAnalyticsConsentState() === 'granted' && isUtagViewReady()) {
		return;
	}

	intervalId = window.setInterval(trySendView, POLL_MS);
}

/**
 * Variation attribute ids that must not be used as product category.
 */
let VARIANT_ATTR_TOKENS = {
	packsize: true,
	packtype: true,
	varpack: true,
	pack: true,
	color: true,
	size: true
};

/**
 * @param {*} value
 * @returns {boolean}
 */
function isVariantAttrToken(value) {
	return !!VARIANT_ATTR_TOKENS[sanitizeLinkText(value)];
}

/**
 * @param {Object} cat
 * @returns {boolean}
 */
function isRealTrackingCategory(cat) {
	let id;
	let name;

	if (!cat) {
		return false;
	}

	id = cat.id || cat.ID || '';
	name = cat.name || cat.displayName || '';

	if (!id && !name) {
		return false;
	}

	return !isVariantAttrToken(id) && !isVariantAttrToken(name);
}

/**
 * @param {*} value
 * @returns {string}
 */
function firstScalar(value) {
	if (value == null || value === '') {
		return '';
	}

	if (Object.prototype.toString.call(value) === '[object Array]') {
		return value.length ? String(value[0] == null ? '' : value[0]) : '';
	}

	return String(value);
}

/**
 * @param {Object} product
 * @returns {{id: string, name: string}}
 */
function resolveModuleCategory(product) {
	let id = sanitizeLinkText(
		(product && product.primaryCategory && (product.primaryCategory.id || product.primaryCategory.ID)) || ''
	);
	let name = collapseRepeatedName(
		sanitizeLinkText(product && product.primaryCategory && product.primaryCategory.name ? product.primaryCategory.name : '')
	);
	let udId;
	let udName;

	if (isVariantAttrToken(id)) {
		id = '';
	}

	if (isVariantAttrToken(name)) {
		name = '';
	}

	if ((!id || !name) && product && product.category) {
		let flatCategory = collapseRepeatedName(sanitizeLinkText(String(product.category)));
		if (flatCategory && !isVariantAttrToken(flatCategory)) {
			if (!id) {
				id = flatCategory;
			}
			if (!name) {
				name = flatCategory;
			}
		}
	}

	if ((!id || !name) && typeof window !== 'undefined' && window.utag_data) {
		udId = sanitizeLinkText(firstScalar(window.utag_data.product_category));
		udName = collapseRepeatedName(sanitizeLinkText(firstScalar(window.utag_data.category)));
		if (!id && udId && !isVariantAttrToken(udId)) {
			id = udId;
		}
		if (!name && udName && !isVariantAttrToken(udName)) {
			name = udName;
		}
	}

	return { id: id, name: name };
}

/**
 * @param {Object} product
 * @returns {Object}
 */
/**
 * PDF product_recurring: "recurring_every_<n><unit>" for a recurring (Smart Order Refill) purchase,
 * else "no_recurring". Source: line SOR fields (sorPeriodicity + week/month interval) or the PDP
 * refill select value ("2-week", "1-month").
 * @param {Object} product
 * @returns {string}
 */
function resolveProductRecurring(product) {
	let parts = String((product && product.sorRecurring) || '').split('-');
	let interval = parseInt(parts[0], 10);
	let unit = parts[1] || '';

	if (!unit && product && product.hasSmartOrderRefill) {
		unit = String(product.sorPeriodicity || '').toLowerCase();
		interval = parseInt(unit === 'week' ? product.sorWeekInterval : product.sorMonthInterval, 10);
	}

	if ((unit !== 'week' && unit !== 'month') || !(interval > 0)) {
		return 'no_recurring';
	}

	return 'recurring_every_' + interval + unit + (interval > 1 ? 's' : '');
}

function formatProductModule(product) {
	let formattedProduct = {};
	let category;
	let variation;
	let productName;
	let productId;
	let extractedPrice;
	let salesPrice;
	let currency;
	let quantity;

	if (!product) {
		return formattedProduct;
	}

	category = resolveModuleCategory(product);
	variation = getNormalizedVariationState(product);
	productName = collapseRepeatedName(product.productName || product.name || '');
	productId = product.id || product.product_id || '';
	extractedPrice = extractSalesPrice(product.price);
	salesPrice = extractedPrice.value;
	currency = extractedPrice.currency || product.product_currency || (window.utag_data && window.utag_data.store_currency) || '';
	if (typeof currency === 'string') {
		currency = currency.trim().toUpperCase();
	}
	quantity = parseInt(product.quantity, 10);
	if (isNaN(quantity) || quantity < 1) {
		quantity = 1;
	}

	formattedProduct.product = String(productId);
	formattedProduct.quantity = quantity;
	formattedProduct.price = formatProductNumber(salesPrice);
	formattedProduct.product_name = collapseRepeatedName(sanitizeLinkText(productName));
	formattedProduct.product_id = String(productId);
	formattedProduct.product_img_src = resolveProductImageSrc(product);
	formattedProduct.product_currency = String(currency || '')
		.trim()
		.toUpperCase();
	formattedProduct.product_category = category.id;
	formattedProduct.product_category_name = formattedProduct.product_category
		? formattedProduct.product_category + (formattedProduct.product_name ? '-' + formattedProduct.product_name : '')
		: formattedProduct.product_name;
	formattedProduct.product_variant = variation.variant || product.bundleVariant || '';
	formattedProduct.product_price = formatProductNumber(salesPrice);
	formattedProduct.product_sale_percentage = resolveSalePercentage(product.price);
	formattedProduct.product_promo = resolveProductPromo(product);
	formattedProduct.product_type = resolveTrackingProductType(product);
	formattedProduct.product_callout = resolveProductCallout(product);
	formattedProduct.product_format = variation.format || product.bundleFormat || '';
	formattedProduct.product_display_name = formattedProduct.product_name;
	formattedProduct.product_powerbrand = resolveClientPowerBrand(product);
	formattedProduct.product_availability = product.available ? 'available' : 'not_available';
	formattedProduct.product_recurring = resolveProductRecurring(product);

	return formattedProduct;
}

let SUBSCRIPTION_MODULE_KEYS = [
	'subscription_category',
	'subscription_type',
	'subscription_frequency',
	'subscription_product_name',
	'subscription_product_sku',
	'product_sku_group',
	'subscription_product_quantity',
	'subscription_product_price'
];

let SUBSCRIPTION_MODULE_TEXT_KEYS = {
	subscription_category: true,
	subscription_type: true,
	subscription_frequency: true,
	subscription_product_name: true
};

let SUBSCRIPTION_MODULE_NULL_KEYS = {
	subscription_product_sku: true
};

/**
 * Ecommerce subscription module (8 PDF fields). Does not include product module fields.
 * @param {Object} raw
 * @returns {Object|null}
 */
function formatSubscriptionModule(raw) {
	let formatted = {};
	let i;
	let key;
	let value;

	if (!raw || typeof raw !== 'object') {
		return null;
	}

	for (i = 0; i < SUBSCRIPTION_MODULE_KEYS.length; i++) {
		key = SUBSCRIPTION_MODULE_KEYS[i];
		value = raw[key];

		if (SUBSCRIPTION_MODULE_NULL_KEYS[key]) {
			formatted[key] = value == null || value === '' || value === 'null' ? null : String(value);
			continue;
		}

		if (value == null || value === 'null') {
			value = '';
		} else {
			value = String(value);
		}

		if (SUBSCRIPTION_MODULE_TEXT_KEYS[key]) {
			value = sanitizeLinkText(value);
		}

		formatted[key] = value;
	}

	return formatted;
}

function getEmptySubscriptionModule() {
	let module = {};
	let i;

	for (i = 0; i < SUBSCRIPTION_MODULE_KEYS.length; i++) {
		module[SUBSCRIPTION_MODULE_KEYS[i]] = SUBSCRIPTION_MODULE_NULL_KEYS[SUBSCRIPTION_MODULE_KEYS[i]] ? null : '';
	}

	return module;
}

/**
 * Always the 8 PDF subscription module fields.
 * @param {Object} [raw]
 * @returns {Object}
 */
function getSubscriptionModule(raw) {
	return formatSubscriptionModule(raw) || getEmptySubscriptionModule();
}

function getPageSubscriptionModuleRaw() {
	let $el;

	if (typeof $ === 'undefined') {
		return null;
	}

	$el = $('[data-tealium-subscription-module]')
		.filter(function hasSubscriptionModuleAttr() {
			return String($(this).attr('data-tealium-subscription-module') || '').trim();
		})
		.first();

	return parseProductTrackingJson($el.attr('data-tealium-subscription-module'));
}

/** Ecommerce core tracking */

let PDP_TEALIUM_SELECTOR = '.product-wrapper[data-tealium-product], .product-detail[data-tealium-product]';

function decodeTrackingAttr(raw) {
	let variants = [raw];
	let first = raw.charAt(0);

	try {
		variants.push(decodeURIComponent(raw));
	} catch (e) {
		// keep raw
	}

	if (first === '{' || first === '[' || raw.startsWith('%7B') || raw.startsWith('%5B')) {
		return variants;
	}

	try {
		variants.push(decodeBase64Utf8(raw));
	} catch (e2) {
		try {
			if (typeof window !== 'undefined' && typeof window.atob === 'function') {
				variants.push(window.atob(raw));
			}
		} catch (e3) {
			// not base64
		}
	}

	return variants;
}

function parseTrackingJson(raw, wantArray) {
	let variants;
	let i;
	let parsed;

	if (!raw) {
		return wantArray ? [] : null;
	}

	variants = decodeTrackingAttr(raw);

	for (i = 0; i < variants.length; i++) {
		try {
			parsed = JSON.parse(variants[i]);
			if (wantArray) {
				if (Array.isArray(parsed)) {
					return parsed;
				}
			} else if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
				return parsed;
			}
		} catch (e) {
			// try next candidate
		}
	}

	return wantArray ? [] : null;
}

function parseProductTrackingJson(raw) {
	return parseTrackingJson(raw, false);
}

function parseProductModulesJson(raw) {
	return parseTrackingJson(raw, true);
}

function parseFormattedProductModules(raw) {
	return parseProductModulesJson(raw).map(formatProductModule);
}

/**
 * @param {jQuery} $context
 * @returns {number}
 */
function getQuantityFromEcommerceContext($context) {
	let $scope = $context && $context.length ? $context : $('body');
	let $root = $scope.closest('.product-tile-shell');
	let qty;

	if (!$root.length) {
		$root = $scope.closest('.product-detail, .product-wrapper');
	}

	// SOR PDP renders one qty stepper per purchase option: read the checked one, like detail.js getStepperQuantityField.
	let $qty = $root
		.find('.product-refill-options.lvz-pdp-refill input[type=radio]:checked')
		.closest('.lvz-pdp-refill__option')
		.find('select.product-qty, input.product-qty');

	if (!$qty.length) {
		$qty = $root.find('select.product-qty, input.product-qty');
	}

	qty = parseInt($qty.first().val(), 10);
	return !isNaN(qty) && qty > 0 ? qty : 1;
}

/**
 * @param {Object} product
 * @param {number} quantity
 * @returns {Object}
 */
function buildPlainProductTrackingObject(product, quantity, previous) {
	let images = product.images;
	let primaryCategory = product.primaryCategory;

	if (!isRealTrackingCategory(primaryCategory) && previous && isRealTrackingCategory(previous.primaryCategory)) {
		primaryCategory = previous.primaryCategory;
	}

	if (!resolveProductImageSrc({ images: images, imageSrc: product.imageSrc }) && previous) {
		images = previous.images;
	}

	return {
		id: product.id,
		productName: product.productName,
		quantity: quantity,
		price: product.price,
		primaryCategory: primaryCategory,
		images: images,
		imageSrc: resolveProductImageSrc({ images: images, imageSrc: product.imageSrc || (previous && previous.imageSrc) }),
		variationAttributes: product.variationAttributes,
		productType: product.productType,
		brand: product.brand,
		available: product.available,
		availability: product.availability,
		renderedPromotions: product.renderedPromotions,
		// product.isSubscription on catalog models = "subscription available", not a subscription line.
		isSubscription: !!product.LVZ_IsSubscription,
		tags: product.tags || null
	};
}

function buildEcommerceProductPayload(product, quantity) {
	if (!product) {
		return null;
	}

	return formatProductModule(
		$.extend({}, product, {
			quantity: quantity || product.quantity || 1
		})
	);
}

function buildEcommerceProductsPayload(product, quantity) {
	let formatted = buildEcommerceProductPayload(product, quantity);
	return formatted ? [formatted] : [];
}

function getProductUnitPrice(product) {
	let extracted;
	let n;

	if (!product) {
		return NaN;
	}

	extracted = extractSalesPrice(product.price);
	if (extracted.value != null && extracted.value !== '') {
		n = Number(extracted.value);
		if (!isNaN(n)) {
			return n;
		}
	}

	n = Number(product.product_price);
	return isNaN(n) ? NaN : n;
}

/**
 * @param {jQuery} $el
 * @returns {string}
 */
function resolveEcommerceSiteContext($el) {
	let siteType = '';

	if ($el && $el.length) {
		siteType = String($el.closest('[data-tealium-site-type]').attr('data-tealium-site-type') || '').trim();
	}

	if (!siteType) {
		siteType = String($('.product-tile-shell[data-tealium-site-type]').first().attr('data-tealium-site-type') || '').trim();
	}

	return sanitizeLinkText(siteType);
}

/**
 * @returns {boolean}
 */
function isBusinessProductPageContext() {
	return String(getUtagBasePayload().product_page_type || '').trim() === 'business';
}

/**
 * Listing and full site tile CTA event name (B2B prefix when product_page_type is business).
 * @param {string} ctaType raw data-tealium-cta value
 * @returns {string}
 */
function resolveListingCtaEventName(ctaType) {
	let cta = sanitizeLinkText(ctaType);
	let eventName;

	if (!cta) {
		return '';
	}

	eventName = cta === 'direct_link' ? 'direct_link_proceed' : cta;

	if (isBusinessProductPageContext()) {
		return 'b2b-' + eventName;
	}

	return eventName;
}

/**
 * @param {string} eventName
 * @param {Array<Object>} [products]
 * @param {Object} [extra]
 */
function sendEcommerceLinkEvent(eventName, products, extra) {
	let payload = $.extend({}, extra || {}, {
		event_name: eventName
	});

	if (products && products.length) {
		payload.products = products;
	}

	sendUtagLinkEvent(payload);
}

function getProductFromTileShell($shell) {
	if (!$shell || !$shell.length) {
		return null;
	}

	return parseProductTrackingJson($shell.attr('data-tealium-product'));
}

function getPdpProductEl($from) {
	let $root = $from && $from.length ? $from.closest('.product-detail, .product-wrapper') : $();

	if ($root.length && $root.attr('data-tealium-product')) {
		return $root;
	}

	return $(PDP_TEALIUM_SELECTOR).first();
}

function getProductFromPdp($from) {
	return parseProductTrackingJson(getPdpProductEl($from).attr('data-tealium-product'));
}

function syncTealiumProductAttr($el, product) {
	if (!$el || !$el.length || !product) {
		return;
	}

	try {
		let previous = parseProductTrackingJson($el.attr('data-tealium-product'));
		$el.attr(
			'data-tealium-product',
			encodeBase64Utf8(
				JSON.stringify(buildPlainProductTrackingObject(product, getQuantityFromEcommerceContext($el), previous))
			)
		);
	} catch (e) {
		// keep previous tracking payload on serialization failure
	}
}

function syncTileTealiumProduct($shell, product) {
	syncTealiumProductAttr($shell, product);
}

/**
 * Buy-online modal moves the panel out of the tile; resolve shell from panel id when needed.
 * @param {jQuery} $btn
 * @returns {jQuery}
 */
function resolveListingCtaTileShell($btn) {
	let $shell = $btn.closest('.product-tile-shell');

	if ($shell.length) {
		return $shell;
	}

	let $pdp = $btn.closest('.product-detail, .product-wrapper');

	if ($pdp.length) {
		return $pdp;
	}

	let $panel = $btn.closest('.product-tile__buy-online-modal-panel');

	if (!$panel.length) {
		return $shell;
	}

	let panelId = String($panel.attr('id') || '');
	let prefixes = ['lvz-buy-online-panel-sticky-', 'lvz-buy-online-panel-pdp-', 'lvz-buy-online-panel-'];
	let pid = '';
	let i;

	for (i = 0; i < prefixes.length; i++) {
		if (panelId.startsWith(prefixes[i])) {
			pid = panelId.substring(prefixes[i].length);
			break;
		}
	}

	if (!pid) {
		return $shell;
	}

	$shell = $('.product-tile-shell[data-pid="' + pid + '"], .product-tile-shell[data-selected-pid="' + pid + '"]').first();

	if ($shell.length) {
		return $shell;
	}

	return $('.product-detail[data-pid="' + pid + '"], .product-wrapper[data-pid="' + pid + '"]').first();
}

function getProductFromCartLineItem($lineItem) {
	if (!$lineItem || !$lineItem.length) {
		return null;
	}

	let product = parseProductTrackingJson($lineItem.attr('data-tealium-product'));

	if (product) {
		let qty = parseInt($lineItem.find('.quantity').val(), 10);
		if (!isNaN(qty) && qty > 0) {
			product.quantity = qty;
		}
	}

	return product;
}

function syncPdpTealiumProduct(product) {
	if (!product || !product.id) {
		return;
	}

	syncTealiumProductAttr(getPdpProductEl(), product);
}

function getPageAction() {
	let pageEl = document.querySelector('.page[data-action]');
	return pageEl ? pageEl.getAttribute('data-action') : '';
}

function isCartShowPage() {
	return getPageAction() === 'Cart-Show';
}

function isProductShowPage() {
	return getPageAction() === 'Product-Show';
}

/**
 * Shared by every "read this one query param off window.location.search" call site —
 * they used to each carry their own copy of this regex + decode + trim logic.
 * @param {string} name
 * @returns {string}
 */
function getUrlQueryParam(name) {
	let match = String(window.location.search || '').match(new RegExp('[?&]' + name + '=([^&]*)'));

	if (!match) {
		return '';
	}

	try {
		return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim();
	} catch (e) {
		return String(match[1] || '').trim();
	}
}

/**
 * @param {*} value
 * @returns {boolean}
 */
function isConsentLikeValue(value) {
	if (value === true || value === false || value === 0 || value === 1) {
		return true;
	}

	let normalized = String(value).toLowerCase().trim();
	return normalized === 'yes' || normalized === 'no' || normalized === 'true' || normalized === 'false';
}

/**
 * @param {Object} formData
 * @param {string[]} keys
 * @returns {*}
 */
function pickExactFormValue(formData, keys) {
	if (!formData || typeof formData !== 'object') {
		return undefined;
	}

	for (let i = 0; i < keys.length; i++) {
		if (Object.prototype.hasOwnProperty.call(formData, keys[i])) {
			let value = formData[keys[i]];
			if (value !== undefined && value !== null && value !== '') {
				return value;
			}
		}
	}

	return undefined;
}

/**
 * @param {*} data
 * @param {string} key
 * @returns {string}
 */
function getSerializedFormValue(data, key) {
	if (!data || !key) {
		return '';
	}

	if (typeof data === 'string') {
		try {
			return String(new URLSearchParams(data).get(key) || '');
		} catch (e) {
			return '';
		}
	}

	if (typeof data === 'object' && data[key] != null) {
		return String(data[key]);
	}

	return '';
}

/**
 * @param {*} data
 * @returns {Object|null}
 */
function parseAjaxJsonPayload(data) {
	if (!data) {
		return null;
	}

	if (typeof data === 'object' && !Array.isArray(data)) {
		return data;
	}

	if (typeof data === 'string') {
		let trimmed = data.trim();

		if (!trimmed.startsWith('{')) {
			return null;
		}

		try {
			return JSON.parse(trimmed);
		} catch (e) {
			return null;
		}
	}

	return null;
}

module.exports = {
	normalizeWidgetToken: normalizeWidgetToken,
	sanitizeLinkText: sanitizeLinkText,
	trackingMarkupToPlainText: trackingMarkupToPlainText,
	trackingEditorialAttrSerialized: trackingEditorialAttrSerialized,
	getUtagBasePayload: getUtagBasePayload,
	sendUtagLinkEvent: sendUtagLinkEvent,
	sendUtagViewEvent: sendUtagViewEvent,
	formatProductModule: formatProductModule,
	formatSubscriptionModule: formatSubscriptionModule,
	getSubscriptionModule: getSubscriptionModule,
	getPageSubscriptionModuleRaw: getPageSubscriptionModuleRaw,
	formatNotifyMeVariantType: formatNotifyMeVariantType,
	flattenProductModules: flattenProductModules,
	parseProductTrackingJson: parseProductTrackingJson,
	parseProductModulesJson: parseProductModulesJson,
	parseFormattedProductModules: parseFormattedProductModules,
	getQuantityFromEcommerceContext: getQuantityFromEcommerceContext,
	buildEcommerceProductPayload: buildEcommerceProductPayload,
	buildEcommerceProductsPayload: buildEcommerceProductsPayload,
	getProductUnitPrice: getProductUnitPrice,
	resolveEcommerceSiteContext: resolveEcommerceSiteContext,
	isBusinessProductPageContext: isBusinessProductPageContext,
	resolveListingCtaEventName: resolveListingCtaEventName,
	sendEcommerceLinkEvent: sendEcommerceLinkEvent,
	getProductFromTileShell: getProductFromTileShell,
	getProductFromPdp: getProductFromPdp,
	getPdpProductEl: getPdpProductEl,
	syncTileTealiumProduct: syncTileTealiumProduct,
	resolveListingCtaTileShell: resolveListingCtaTileShell,
	getProductFromCartLineItem: getProductFromCartLineItem,
	syncPdpTealiumProduct: syncPdpTealiumProduct,
	isCartShowPage: isCartShowPage,
	isProductShowPage: isProductShowPage,
	getUrlQueryParam: getUrlQueryParam,
	isConsentLikeValue: isConsentLikeValue,
	pickExactFormValue: pickExactFormValue,
	getSerializedFormValue: getSerializedFormValue,
	parseAjaxJsonPayload: parseAjaxJsonPayload
};
